import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CoreLanguageService } from '../../services/language.service';
import { CoreLogService } from '../../services/log.service';
import Utils from '../../shared/utils';

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isPast: boolean;
  hasContentForTheDay: boolean;
  isSelected: boolean;
  isToday: boolean;
}

interface CalendarLanguageConfig {
  weekDays: string[];
  weekDaysShort: string[];
  months: string[];
  monthsShort: string[];
}

enum CalendarView {
  Day = 'day',
  Month = 'month',
  Year = 'year'
}

/** Multiplier to extract year from YYYYMMDD integer */
const YMD_YEAR_DIVISOR = 10000;

/** Multiplier to extract month from YYYYMMDD integer */
const YMD_MONTH_DIVISOR = 100;

/** Total cells in a 6-row × 7-column calendar grid */
const CALENDAR_GRID_SIZE = 42;
/**
 * First day of the month in YMD format is 1,
 *  in JS Date it's 0, so we need to add 1 when converting between them
 */
const FIRST_DAY_OF_MONTH = 1;

/** Last day before the next month */
const LAST_DAY_BEFORE_NEXT_MONTH = 0;

/** January month index (0-based) */
const JANUARY = 0;

/** December month index (0-based) */
const DECEMBER = 11;

@Component({
  selector: 'ya-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarComponent implements OnInit, OnChanges {

  @Input() availableDates: { from: number, to: number }[] = [];
  @Input() mode: 'default' | 'ticket' = 'default';
  @Output() dateSelected = new EventEmitter<Date>();

  public readonly CalendarView = CalendarView;
  public currentDate: Date = new Date();
  public selectedDate: Date = null;
  public currentView: CalendarView = CalendarView.Day;
  public calendarDays: (CalendarDay | null)[] = [];
  public weekDays: string[] = [];
  public weekDaysFull: string[] = []; // Full weekday names for aria-labels
  public months: string[] = [];
  public displayYear: number;
  public displayMonth: number;
  public yearRange: number[] = [];
  public minYear: number;
  public maxYear: number;

  private initialDateEmitted: boolean = false;

  private languageConfig: { [key: string]: CalendarLanguageConfig } = {
    de: {
      weekDays: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
      weekDaysShort: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
      months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      monthsShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    en: {
      weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      weekDaysShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    }
  };

  constructor(
    private _languageService: CoreLanguageService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService
  ) { }

  ngOnInit() {
    const me = this;
    me.setLanguageStrings();
    me.calculateYearRange();

    //initialize date
    const initialDate = me.findInitialDate();
    me.selectedDate = initialDate;
    me.displayYear = initialDate.getFullYear();
    me.displayMonth = initialDate.getMonth();

    me.generateCalendar();
    me.PushChanges();

    if (!me.initialDateEmitted && me.checkDateHasContent(initialDate)) {
      me.initialDateEmitted = true;
      me.dateSelected.emit(initialDate);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const me = this;
    if (changes['availableDates']) {
      me.calculateYearRange();
      if (!me.initialDateEmitted && me.mode === 'ticket' && me.availableDates && me.availableDates.length > 0) {
        const initialDate = me.findInitialDate();
        me.selectedDate = initialDate;
        me.displayYear = initialDate.getFullYear();
        me.displayMonth = initialDate.getMonth();

        me.generateCalendar();
        me.PushChanges();

        // Emit initial date selection
        if (me.checkDateHasContent(initialDate)) {
          me.initialDateEmitted = true;
          me.dateSelected.emit(initialDate);
        }
      } else {
        me.generateCalendar();
        me.PushChanges();
      }
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("CalendarComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** Reset calendar state when switching menu groups */
  public resetForNewGroup(newDates?: { from: number, to: number }[]): void {
    const me = this;
    me.initialDateEmitted = false;

    // Update availableDates if provided
    if (newDates) {
      me.availableDates = newDates;
      me.calculateYearRange();
    }

    const firstAvailable = me.findInitialDate();
    me.selectedDate = firstAvailable;
    me.displayYear = firstAvailable.getFullYear();
    me.displayMonth = firstAvailable.getMonth();

    me.generateCalendar();
    me.PushChanges();

    if (me.checkDateHasContent(firstAvailable)) {
      me.initialDateEmitted = true;
      me.dateSelected.emit(firstAvailable);
    } else {
      me.dateSelected.emit(null);
    }
  }

  /** set language specific strings */
  private setLanguageStrings(): void {
    const me = this;
    const currentLang = me._languageService.getCurrentLanguage();
    const config = me.languageConfig[currentLang] || me.languageConfig['de'];

    me.weekDays = config.weekDaysShort;
    me.weekDaysFull = config.weekDays;
    me.months = config.months;
  }

  /**
   * Calculates the minimum and maximum year based on the available dates.
   * All dates in availableDates are guaranteed to be valid (no 0 values).
   * If availableDates is empty, the current year is used.
   * In ticket mode, the minimum year is always the current year or later.
   */
  private calculateYearRange(): void {
    const me = this;
    const currentYear = new Date().getFullYear();

    if (me.availableDates && me.availableDates.length > 0) {
      const fromYears = me.availableDates.map(dateRange => Math.floor(dateRange.from / YMD_YEAR_DIVISOR));
      const toYears = me.availableDates.map(dateRange => Math.floor(dateRange.to / YMD_YEAR_DIVISOR));
      const allYears = [...fromYears, ...toYears];

      me.minYear = Math.min(...allYears);
      me.maxYear = Math.max(...allYears);

      // In ticket mode, don't show years before current year
      if (me.mode === 'ticket' && me.minYear < currentYear) {
        me.minYear = currentYear;
      }
    } else {
      me.minYear = currentYear;
      me.maxYear = currentYear;
    }

    me.updateYearRange();
  }

  /** Updates the year range list based on the current minimum and maximum year. */
  private updateYearRange(): void {
    const me = this;
    me.yearRange = [];
    for (let year = me.minYear; year <= me.maxYear; year++) {
      me.yearRange.push(year);
    }
  }

  /**
 * Builds the calendar grid for the current month,
 * including leading and trailing placeholders
 * to ensure a fixed grid size.
 */
  private generateCalendar(): void {
    const me = this;
    me.calendarDays = [];

    const firstDayOfMonth = new Date(me.displayYear, me.displayMonth, FIRST_DAY_OF_MONTH);
    const lastDayOfMonth = new Date(me.displayYear, me.displayMonth + FIRST_DAY_OF_MONTH, LAST_DAY_BEFORE_NEXT_MONTH);

    // Start with Monday as the first day of the week
    let firstWeekdayIndex = firstDayOfMonth.getDay() - 1;
    if (firstWeekdayIndex === -1) firstWeekdayIndex = 6;

    // Add empty placeholders for previous month's trailing days
    for (let placeholderIndex = 0; placeholderIndex < firstWeekdayIndex; placeholderIndex++) {
      me.calendarDays.push(null);
    }

    // Add current month's days
    for (let dayNumber = 1; dayNumber <= lastDayOfMonth.getDate(); dayNumber++) {
      const date = new Date(me.displayYear, me.displayMonth, dayNumber);
      me.calendarDays.push(me.createCalendarDay(date, true));
    }

    // Add empty placeholders for next month's leading days
    const remainingCells = CALENDAR_GRID_SIZE - me.calendarDays.length;
    for (let placeholderIndex = 0; placeholderIndex < remainingCells; placeholderIndex++) {
      me.calendarDays.push(null);
    }
  }

  /**
   * Creates a Day object with all state flags
   * (past, today, selected, has content) for the given date.
   * @param date The date for which to create the Day object
   * @param isCurrentMonth Whether the date belongs to the current month
   * @returns A Day object representing the given date
   */
  private createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const me = this;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const isPast = normalizedDate < today;
    const hasContentForTheDay = me.checkDateHasContent(normalizedDate);
    const isSelected = me.selectedDate && me.isSameDate(normalizedDate, me.selectedDate);
    const isToday = me.isSameDate(normalizedDate, today);

    return {
      date: date,
      dayNumber: date.getDate(),
      isCurrentMonth: isCurrentMonth,
      isPast: isPast,
      hasContentForTheDay: hasContentForTheDay,
      isSelected: isSelected,
      isToday: isToday
    };
  }

  /**
   * Checks whether the given date falls within any available date range,
   * considering open start/end restrictions and special mode rules.
   * @param date The date to check for content availability
   * @returns  True if the date falls within any of the available date ranges.
   */
  private checkDateHasContent(date: Date): boolean {
    const me = this;
    if (!me.availableDates || me.availableDates.length === 0) return false;


    const dateAsYmd = date.getFullYear() * YMD_YEAR_DIVISOR
      + (date.getMonth() + FIRST_DAY_OF_MONTH) * YMD_MONTH_DIVISOR
      + date.getDate();

    return me.availableDates.some(dateRange => {
      if (me.mode === 'ticket') {
        return dateRange.from === dateAsYmd;
      }
      const isAfterStart = dateAsYmd >= dateRange.from;
      const isBeforeEnd = dateAsYmd <= dateRange.to;

      return isAfterStart && isBeforeEnd;
    });
  }

  /** Compares dates */
  private isSameDate(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  /** Finds the initial date for calendar */
  private findInitialDate(): Date {
    const me = this;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (me.checkDateHasContent(today)) {
      return today;
    }

    // Find next available date
    if (!me.availableDates || me.availableDates.length === 0) {
      return today; // Fallback to today
    }

    const todayYmd = today.getFullYear() * YMD_YEAR_DIVISOR
      + (today.getMonth() + FIRST_DAY_OF_MONTH) * YMD_MONTH_DIVISOR
      + today.getDate();

    // Collect all available dates
    const futureDates: number[] = [];
    me.availableDates.forEach(range => {
      if (range.from >= todayYmd) {
        futureDates.push(range.from);
      } else if (range.to >= todayYmd) {
        futureDates.push(todayYmd);
      }
    });

    if (futureDates.length === 0) {
      return today; // Fallback
    }

    const nextAvailableYmd = Math.min(...futureDates);
    const year = Math.floor(nextAvailableYmd / YMD_YEAR_DIVISOR);
    const month = Math.floor((nextAvailableYmd % YMD_YEAR_DIVISOR) / YMD_MONTH_DIVISOR) - FIRST_DAY_OF_MONTH;
    const day = nextAvailableYmd % YMD_MONTH_DIVISOR;

    return new Date(year, month, day);
  }

  /** Handles click events on calendar days.*/
  public onDayClick(day: CalendarDay): void {
    const me = this;
    if (!day.isCurrentMonth) {
      return;
    }
    me.selectedDate = new Date(day.date);
    me.displayYear = me.selectedDate.getFullYear();
    me.displayMonth = me.selectedDate.getMonth();
    me.generateCalendar();
    me.PushChanges();
    me.dateSelected.emit(me.selectedDate);
  }

  /** Navigate to previous month */
  public previousMonth(): void {
    const me = this;
    me.displayMonth--;
    if (me.displayMonth < JANUARY) {
      me.displayMonth = DECEMBER;
      me.displayYear--;
    }
    if (me.mode === 'ticket') {
      me.selectBestDateForMonth(FIRST_DAY_OF_MONTH);
    }

    me.generateCalendar();
    me.PushChanges();
  }

  /** Navigate to next month */
  public nextMonth(): void {
    const me = this;
    me.displayMonth++;
    if (me.displayMonth > DECEMBER) {
      me.displayMonth = JANUARY;
      me.displayYear++;
    }
    if (me.mode === 'ticket') {
      me.selectBestDateForMonth(FIRST_DAY_OF_MONTH);
    }

    me.generateCalendar();
    me.PushChanges();
  }

  /**
   * Selects best available date in current month after navigation
   * @param preferredDay Preferred day of month
   */
  private selectBestDateForMonth(preferredDay: number): void {
    const me = this;
    const targetDate = new Date(me.displayYear, me.displayMonth, preferredDay);
    if (me.checkDateHasContent(targetDate) && !Utils.isPastDate(targetDate)) {
      me.selectedDate = targetDate;
      me.dateSelected.emit(targetDate);
      return;
    }
    const firstAvailableInMonth = me.findFirstAvailableDateInMonth();
    if (firstAvailableInMonth) {
      me.selectedDate = firstAvailableInMonth;
      me.dateSelected.emit(firstAvailableInMonth);
      return;
    }
    // No available dates in month, clear selection
    me.selectedDate = null;
    me.dateSelected.emit(null);
  }

  /**
   * Finds first available date in current displayed month
   */
  private findFirstAvailableDateInMonth(): Date | null {
    const me = this;
    const daysInMonth = new Date(me.displayYear, me.displayMonth + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(me.displayYear, me.displayMonth, day);
      if (me.checkDateHasContent(date) && !Utils.isPastDate(date)) {
        return date;
      }
    }

    return null;
  }

  /** Navigate to previous year */
  public previousYear(): void {
    const me = this;
    if (me.displayYear > me.minYear) {
      me.displayYear--;
      me.resetToCurrentDateIfNeeded();
      me.generateCalendar();
      me.PushChanges();
    }
  }

  /** Navigate to next year */
  public nextYear(): void {
    const me = this;
    if (me.displayYear < me.maxYear) {
      me.displayYear++;
      me.resetToCurrentDateIfNeeded();
      me.generateCalendar();
      me.PushChanges();
    }
  }

  /** Reset date if navigating back to current year */
  private resetToCurrentDateIfNeeded(): void {
    const me = this;
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    if (me.displayYear === currentYear) {
      me.displayMonth = currentMonth;
      me.selectedDate = new Date(today);
    }
  }

  /** Navigate to previous year in year view */
  public previousYearInYearView(): void {
    const me = this;
    if (me.displayYear > me.minYear) {
      me.displayYear--;
      me.PushChanges();
    }
  }

  /** Navigate to next year in year view */
  public nextYearInYearView(): void {
    const me = this;
    if (me.displayYear < me.maxYear) {
      me.displayYear++;
      me.PushChanges();
    }
  }

  /** Switch to month view */
  public showMonthView(): void {
    const me = this;
    me.currentView = CalendarView.Month;
    me.PushChanges();
  }


  /** Switch to year view */
  public showYearView(): void {
    const me = this;
    me.currentView = CalendarView.Year;
    me.PushChanges();
  }

  /** Select a month */
  public selectMonth(monthIndex: number): void {
    const me = this;
    // Allow selecting past months
    me.displayMonth = monthIndex;
    me.currentView = CalendarView.Day;
    if (me.mode === 'ticket') {
      me.selectBestDateForMonth(FIRST_DAY_OF_MONTH);
    }
    me.generateCalendar();
    me.PushChanges();
  }

  /** Select a year */
  public selectYear(year: number): void {
    const me = this;
    me.displayYear = year;
    me.currentView = CalendarView.Month;
    me.PushChanges();
  }

  /** Checks if a month is in the past */
  public isMonthPast(monthIndex: number): boolean {
    const me = this;
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    if (me.displayYear < currentYear) return true;
    if (me.displayYear === currentYear && monthIndex < currentMonth) return true;
    return false;
  }

  /** Checks if a month has available tickets/content */
  public hasMonthContent(monthIndex: number, year?: number): boolean {
    const me = this;
    if (!me.availableDates || me.availableDates.length === 0) {
      return false;
    }

    const checkYear = year !== undefined ? year : me.displayYear;
    const monthStartYmd = checkYear * YMD_YEAR_DIVISOR + (monthIndex + FIRST_DAY_OF_MONTH) * YMD_MONTH_DIVISOR + 1;
    const monthEndYmd = checkYear * YMD_YEAR_DIVISOR + (monthIndex + FIRST_DAY_OF_MONTH) * YMD_MONTH_DIVISOR + 31;

    // Check if any available date falls within this month
    // All dates are guaranteed to be valid (no 0 values from service)
    return me.availableDates.some(range => {
      // Check if range overlaps with month
      return (range.from <= monthEndYmd && range.to >= monthStartYmd);
    });
  }

  /** Checks if a year is in the past */
  public isYearPast(year: number): boolean {
    return year < new Date().getFullYear();
  }

  /** Get the short name of a month */
  public getMonthShortName(index: number): string {
    const me = this;
    const currentLang = me._languageService.getCurrentLanguage();
    const config = me.languageConfig[currentLang] || me.languageConfig['de'];
    return config.monthsShort[index];
  }

  /** Get the name of the current month */
  public get currentMonthName(): string {
    const me = this;
    return me.months[me.displayMonth];
  }

  /** Check if navigation to the previous year in year view is possible */
  public get canNavigatePrevYearInYearView(): boolean {
    const me = this;
    return me.displayYear > me.minYear;
  }

  /** Check if navigation to the next year in year view is possible */
  public get canNavigateNextYearInYearView(): boolean {
    const me = this;
    return me.displayYear < me.maxYear;
  }

  /** Check if navigation to the previous month is possible*/
  public get canNavigatePrevMonth(): boolean {
    const me = this;
    // Calculate previous month
    let prevYear = me.displayYear;
    let prevMonth = me.displayMonth - 1;
    if (prevMonth < JANUARY) {
      prevMonth = DECEMBER;
      prevYear--;
    }

    // no navigation outside year range
    return prevYear >= me.minYear;
  }

  /** Check if previous month should be grayed out (past OR no content) */
  public get isPrevMonthGrayedOut(): boolean {
    const me = this;
    let prevYear = me.displayYear;
    let prevMonth = me.displayMonth - 1;
    if (prevMonth < JANUARY) {
      prevMonth = DECEMBER;
      prevYear--;
    }
    const isPast = me.isMonthPastInYear(prevMonth, prevYear);
    const hasNoContent = !me.hasMonthContent(prevMonth, prevYear);
    return isPast || hasNoContent;
  }

  /** Check if a specific month/year combination is in the past */
  private isMonthPastInYear(monthIndex: number, year: number): boolean {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    if (year < currentYear) return true;
    if (year === currentYear && monthIndex < currentMonth) return true;
    return false;
  }

  /** Check if navigation to the next month is possible (based on year range only) */
  public get canNavigateNextMonth(): boolean {
    const me = this;

    // Calculate next month
    let nextYear = me.displayYear;
    let nextMonth = me.displayMonth + 1;
    if (nextMonth > DECEMBER) {
      nextMonth = JANUARY;
      nextYear++;
    }

    // Only block navigation outside year range (minYear to maxYear)
    return nextYear <= me.maxYear;
  }

  /** Check if next month should be grayed out (past OR no content) */
  public get isNextMonthGrayedOut(): boolean {
    const me = this;
    let nextYear = me.displayYear;
    let nextMonth = me.displayMonth + 1;
    if (nextMonth > DECEMBER) {
      nextMonth = JANUARY;
      nextYear++;
    }
    const isPast = me.isMonthPastInYear(nextMonth, nextYear);
    const hasNoContent = !me.hasMonthContent(nextMonth, nextYear);

    return isPast || hasNoContent;
  }

  //#region Accessibility

  /** Get ARIA label for calendar day with proper internationalization */
  public getDayAriaLabel(day: CalendarDay): string {
    const me = this;

    // Base label with date
    let baseKey = 'phA11Y_calendar_day';
    if (day.isToday && day.isSelected) {
      baseKey = 'phA11Y_calendar_day_today';
    } else if (day.isSelected) {
      baseKey = 'phA11Y_calendar_day_selected';
    } else if (day.isToday) {
      baseKey = 'phA11Y_calendar_day_today';
    }

    let label = me._languageService.value(baseKey)
      .replace('{day}', day.dayNumber.toString())
      .replace('{month}', me.currentMonthName)
      .replace('{year}', me.displayYear.toString());

    // Add availability status
    if (day.hasContentForTheDay) {
      label += ', ' + me._languageService.value('phA11Y_calendar_day_available');
    } else {
      label += ', ' + me._languageService.value('phA11Y_calendar_day_unavailable');
    }

    return label;
  }

  private get hasNoContent(): boolean {
    const me = this;
    return !me.currentMonthName || me.displayYear === undefined || me.displayYear === null;
  }

  /** Get ARIA label for month/year selection header */
  public getHeaderAriaLabel(): string {
    const me = this;
    if (me.hasNoContent) {
      return '';
    }
    return me._languageService.value('phA11Y_calendar_select_month')
      .replace('{month}', me.currentMonthName)
      .replace('{year}', me.displayYear.toString());
  }

  /** Get ARIA label for year selection header */
  public getYearHeaderAriaLabel(): string {
    const me = this;
    if (me.hasNoContent) {
      return '';
    }
    return me._languageService.value('phA11Y_calendar_select_year')
      .replace('{year}', me.displayYear.toString());
  }

  /** Get ARIA label for previous month button */
  public get previousMonthAriaLabel(): string {
    return this._languageService.value('phA11Y_calendar_previous_month');
  }

  /** Get ARIA label for next month button */
  public get nextMonthAriaLabel(): string {
    return this._languageService.value('phA11Y_calendar_next_month');
  }

  /** Get ARIA label for previous year button */
  public get previousYearAriaLabel(): string {
    return this._languageService.value('phA11Y_calendar_previous_year');
  }

  /** Get ARIA label for next year button */
  public get nextYearAriaLabel(): string {
    return this._languageService.value('phA11Y_calendar_next_year');
  }

  /** Get ARIA label for year cell */
  public getYearAriaLabel(year: number): string {
    const me = this;
    if (me.hasNoContent) {
      return '';
    }
    return this._languageService.value('phA11Y_calendar_year').replace('{year}', year.toString());
  }
}
